// contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../components/Firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../components/Firebase';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [userInfo, setUserInfo] = useState(null);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [vipModeEnabled, setVipModeEnabled] = useState(false);

  useEffect(() => {
    // === [DEBUG] WYKRYWANIE ŚRODOWISKA I ZMIENNYCH ===
    console.log("%c[Symuliga VIP Debug] --- INICJALIZACJA KONTEKSTU AUTH ---", "color: #00bcd4; font-weight: bold;");
    
    // NAPRAWA: Webpack (DefinePlugin) wymaga sztywnego zapisu bez '?.', aby poprawnie wstrzyknąć zmienne.
    let craKey = undefined;
    try {
      craKey = process.env.REACT_APP_FIREBASE_VIP_KEY;
    } catch (e) {
      // Środowisko nie obsługuje process.env (np. czysty Vite)
    }
    
    let viteKey = undefined;
    try {
      viteKey = import.meta.env.VITE_VIP_FLAG_KEY;
    } catch (e) {
      // Środowisko nie obsługuje import.meta
    }

    // Wybór finalnego klucza
    const VIP_FLAG_FIELD = craKey || viteKey || 'vipModeEnabled';
    
    try {
      console.log("[Symuliga VIP Debug] Bieżący URL API (REACT_APP_API_URL):", process.env.REACT_APP_API_URL);
    } catch(e) {}
    console.log("[Symuliga VIP Debug] Pobrany klucz z REACT_APP_FIREBASE_VIP_KEY (CRA):", craKey);
    console.log("[Symuliga VIP Debug] Pobrany klucz z VITE_VIP_FLAG_KEY (Vite):", viteKey);
    console.log("[Symuliga VIP Debug] Używane pole dla flagi VIP w Firestore:", VIP_FLAG_FIELD);
    // ===============================================

    // Nasłuchujemy dokumentu 'global' w kolekcji 'feature_flags'
    const unsubscribeFlag = onSnapshot(doc(db, 'feature_flags', 'global'), (snap) => {
      if (snap.exists()) {
        const rawData = snap.data();
        const fieldValue = rawData[VIP_FLAG_FIELD];
        const isEnabled = fieldValue === true;
        
        console.log(`[Symuliga VIP Debug] Firestore 'feature_flags/global' -> Odczytano pole [${VIP_FLAG_FIELD}]:`, fieldValue);
        console.log("[Symuliga VIP Debug] Wynikowy stan vipModeEnabled (Tryb Testowy VIP):", isEnabled);
        
        setVipModeEnabled(isEnabled);
      } else {
        console.error("[Symuliga VIP Debug] BŁĄD: Dokument 'feature_flags/global' nie istnieje w Twoim Firestore!");
        setVipModeEnabled(false);
      }
    });

    const unsubscribeAuth = auth.onIdTokenChanged(async (user) => {
      setLoading(true);

      if (user) {
        setUserInfo({
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
        });

        try {
          // Wymuszamy pobranie najświeższych uprawnień (claims) z serwera Firebase
          const idTokenResult = await user.getIdTokenResult(true);
          setRoles(idTokenResult.claims || {});
          
          console.log("%c[Symuliga VIP Debug] Zalogowany użytkownik. Uprawnienia (claims):", "color: #4caf50; font-weight: bold;", idTokenResult.claims);
          console.log("[Symuliga VIP Debug] Czy posiada rolę VIP (isVip):", !!idTokenResult.claims?.vip);
          console.log("[Symuliga VIP Debug] Pełny UID użytkownika:", user.uid);
        } catch (err) {
          console.error('[Symuliga VIP Debug] Błąd podczas pobierania claims:', err);
          setRoles({});
        }
      } else {
        setUserInfo(null);
        setRoles({});
        console.log("%c[Symuliga VIP Debug] Status Auth: Użytkownik niezalogowany.", "color: #f44336;");
      }

      setLoading(false);
    });

    return () => {
      unsubscribeFlag();
      unsubscribeAuth();
    };
  }, []);

  const hasRole = (role) => !!roles[role];

  const value = {
    user: userInfo,
    loading,
    roles,
    hasRole,
    isAdmin: hasRole('admin'),
    isVip: hasRole('vip'),
    vipModeEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);